import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile from database
  const fetchUserProfile = async (userId) => {
    try {
      console.log('📥 Fetching profile for user:', userId);
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Profile fetch error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Profile not found');
      }

      console.log('✅ Profile fetched:', data.email, '- Role:', data.role);
      return data;
    } catch (err) {
      console.error('💥 Error fetching profile:', err);
      throw err;
    }
  };

  // Validate user has correct role and is active
  const validateUser = (profile) => {
    if (!profile.is_active) {
      throw new Error('Your account is inactive. Please contact support.');
    }

    if (profile.role !== 'checker_packer') {
      throw new Error(`Access denied. This app is for packers only. Your role: ${profile.role}`);
    }

    return true;
  };

  // Handle authentication state
  const handleAuthChange = async (session) => {
    try {
      console.log('🔄 Auth state changing...');
      setError(null);

      if (!session) {
        console.log('📤 No session - user logged out');
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      console.log('👤 User authenticated:', session.user.email);

      // Fetch and validate profile
      const userProfile = await fetchUserProfile(session.user.id);
      validateUser(userProfile);

      // Set authenticated state
      setSession(session);
      setProfile(userProfile);
      setLoading(false);
      console.log('✅ Authentication complete');

    } catch (err) {
      console.error('❌ Auth validation failed:', err.message);
      setError(err.message);
      setSession(null);
      setProfile(null);
      setLoading(false);
      
      // Sign out on validation failure
      await supabase.auth.signOut();
    }
  };

  // Initialize auth state
  useEffect(() => {
    console.log('🚀 Initializing auth...');

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthChange(session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('🔔 Auth event:', _event);
      handleAuthChange(session);
    });

    return () => {
      console.log('🛑 Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  // Sign in function
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔐 Signing in:', email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      console.log('✅ Sign in successful');
      return { success: true, error: null };

    } catch (err) {
      console.error('❌ Sign in failed:', err.message);
      const errorMessage = err.message === 'Invalid login credentials'
        ? 'Invalid email or password'
        : err.message;
      
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      console.log('👋 Signing out...');
      setLoading(true);
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setSession(null);
      setProfile(null);
      setError(null);
      setLoading(false);
      console.log('✅ Signed out successfully');

    } catch (err) {
      console.error('❌ Sign out error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const value = {
    session,
    profile,
    loading,
    error,
    signIn,
    signOut,
    user: session?.user || null,
    isAuthenticated: !!(session && profile),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
